import { useLazyQuery } from '@apollo/client';
import {
  AllForemanId,
  AllGeneralForemanId,
  AllSuperintendentId,
  bankersRound,
  checkIfUnitOfMeasureIsHours,
  ErrorRetry,
  getUserUnitLabel,
  regularRound,
  StatsSpinner,
  useAppUser,
  useName,
} from '@bechtel/client-common';
import { GET_WEEKLY_MILESTONE_PROGRESS } from '@bechtel/client-gql';
import { Box, Heading, Text } from '@bechtel/gds';
import {
  CrewMember,
  DailyMilestoneProgress,
  MilestoneStatusCode,
  ProjectWeek,
  WeeklyMilestoneAnalysis,
  WeeklyMilestoneProgress,
  WeeklyMilestoneProgressInput,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { format } from 'date-fns';
import {
  compact,
  concat,
  filter,
  find,
  get,
  includes,
  indexOf,
  last,
  map,
  minBy,
  nth,
  some,
  sumBy,
  uniq,
  uniqBy,
} from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/src/component/DefaultTooltipContent';
import { ThemeContext } from 'styled-components';
import { AvatarRegularStyled, ChildContainerStyled } from '../styled';
import { ChartHeaderContainer, ErrorContainer, TooltipContainerStyled } from './styled';
import { ClaimedProgressReferenceArea, TargetIndicatorReferenceArea, WeekDayXAxis } from './utils';
const BAR_SIZE = 32;
const RHOMBUS_SIZE = 8;
export interface ChartProps {
  selectedProjectWeek: ProjectWeek;
  selectedCrewMember: CrewMember | null;
}

interface ChartContentProps {
  selectedProjectWeek: ProjectWeek;
  selectedCrewMember: CrewMember | null;
}

const ChartHeader: FC<ChartContentProps> = ({ selectedProjectWeek, selectedCrewMember }) => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  return (
    <ChartHeaderContainer>
      <Box flexDirection={{ sm: 'column' }} pl={{ sm: '500' }}>
        <Heading size="400" weight="600">
          <Trans>Claimed Progress</Trans>
        </Heading>
        <Heading size="250" weight="600" color="grey.600" style={{ marginTop: '4px' }}>
          {selectedProjectWeek
            ? concat(
                t`Week`,
                ` ${selectedProjectWeek.weekNumber} `,
                `${selectedProjectWeek.isCurrent ? t`(Current)` : ''}`
              )
            : t`No Week`}
        </Heading>
      </Box>
      <Box pr={{ sm: '500' }} style={{ marginLeft: 'auto' }}>
        <Box flexDirection={{ sm: 'column' }} justifyContent={{ sm: 'center' }}>
          <Heading size="400" weight="600">
            {!includes([AllGeneralForemanId, AllSuperintendentId, AllForemanId], selectedCrewMember?.id)
              ? `${getFullName(selectedCrewMember?.firstName, selectedCrewMember?.lastName)}`
              : t`No User Selected`}
          </Heading>
        </Box>
        <Box pl={{ sm: '300' }}>
          <AvatarRegularStyled
            hasImageUrl={selectedCrewMember && selectedCrewMember.imageUrl ? true : false}
            src={selectedCrewMember && selectedCrewMember.imageUrl ? selectedCrewMember.imageUrl : undefined}
          />
        </Box>
      </Box>
    </ChartHeaderContainer>
  );
};

const getToolTipLabelFromFieldName = (fieldName: string) => {
  if (fieldName === 'targetHours' || fieldName === 'targetQuantity') return 'Target';
  else if (fieldName === 'milestonehours' || fieldName === 'milestoneQuantity') return 'Claimed';
  else if (fieldName === 'milestoneClaimedInPlanHours' || fieldName === 'milestoneClaimedInPlanQuantity')
    return 'Claimed in Plan';
  else if (fieldName === 'milestoneApprovedHours' || fieldName === 'milestoneApprovedQuantity') return 'Approved';
  else if (fieldName === 'cumulativeMilestoneHours' || fieldName === 'cumulativeMilestoneQuantity') return 'Cumulative';
};

const CharToolTip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  if (!active || !payload || payload.length == 0 || !payload[0].payload || !payload[0].payload.weekDay) return null;

  return (
    <TooltipContainerStyled>
      <Box pb={{ sm: '400' }}>
        <Heading size="400" weight="600">
          {payload[0].payload.weekDay}
        </Heading>
      </Box>
      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
        {map(payload, (chartPayload) => {
          return (
            <>
              {chartPayload && (
                <Box pb={{ sm: '300' }}>
                  <Text size="300" color="grey.600">
                    {chartPayload.name ? `${getToolTipLabelFromFieldName(chartPayload.name?.toString())}  ` : ''}
                  </Text>
                  <Text size="300" weight="600">
                    &nbsp; {chartPayload.value}
                  </Text>
                </Box>
              )}
            </>
          );
        })}
      </Box>
    </TooltipContainerStyled>
  );
};

const ChartContent: FC<ChartContentProps> = ({ selectedProjectWeek, selectedCrewMember }) => {
  const theme = React.useContext(ThemeContext);
  const [getWeeklyMilestoneProgress, { loading, error, data, refetch, called }] = useLazyQuery<
    { weeklyMilestoneProgress: WeeklyMilestoneProgress },
    { input: WeeklyMilestoneProgressInput }
  >(GET_WEEKLY_MILESTONE_PROGRESS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [showHideTooltip, setShowHideTooltip] = useState(false);
  useEffect(() => {
    if (!selectedProjectWeek || !selectedProjectWeek.id || !selectedCrewMember || !selectedCrewMember.persona) return;
    const input: WeeklyMilestoneProgressInput = {
      planningWeekId: selectedProjectWeek.id,
      persona: selectedCrewMember?.persona,
      status: MilestoneStatusCode.CLAIMED,
      isAllCrewMembers: false,
    };
    if (selectedCrewMember.id === AllSuperintendentId) {
      input.isAllCrewMembers = true;
    } else if (selectedCrewMember.id) {
      input.crewMemberId = selectedCrewMember.id;
    }
    getWeeklyMilestoneProgress({
      variables: {
        input: input,
      },
    });
  }, [getWeeklyMilestoneProgress, selectedProjectWeek, selectedCrewMember]);

  const { user } = useAppUser();
  const isUnitOfMeasureHour = checkIfUnitOfMeasureIsHours(user);
  const unitMeasureLabel = getUserUnitLabel(user);

  let milestoneProgress: DailyMilestoneProgress[] = [];
  const milestoneProgressData = get(data, 'weeklyMilestoneProgress.milestoneProgress', []);
  milestoneProgress = milestoneProgressData;
  if (selectedCrewMember && selectedCrewMember.id === AllSuperintendentId && milestoneProgressData.length) {
    const uniqueWeekDays = compact(uniq(map(milestoneProgressData, (item) => item.weekDay)));
    milestoneProgress = map(uniqueWeekDays, (weekDayItem) => {
      const dayData = filter(milestoneProgressData, (item) => item.weekDay === weekDayItem);
      const milestonehours = sumBy(dayData, 'milestonehours') || 0;

      const milestoneQuantity = sumBy(dayData, 'milestoneQuantity') || 0;

      const milestoneApprovedHours = sumBy(dayData, 'milestoneApprovedHours') || 0;

      const milestoneApprovedQuantity = sumBy(dayData, 'milestoneApprovedQuantity') || 0;

      const cumulativeMilestoneHours = sumBy(dayData, 'cumulativeMilestoneHours') || 0;

      const cumulativeMilestoneQuantity = sumBy(dayData, 'cumulativeMilestoneQuantity') || 0;

      const targetHours = sumBy(dayData, 'targetHours') || 0;

      const targetQuantity = sumBy(dayData, 'targetQuantity') || 0;
      const milestoneClaimedInPlanHours = sumBy(dayData, 'milestoneClaimedInPlanHours') || 0;

      const milestoneClaimedInPlanQuantity = sumBy(dayData, 'milestoneClaimedInPlanQuantity') || 0;

      const date = find(milestoneProgressData, (item) => item.weekDay === weekDayItem)?.date;
      const row: DailyMilestoneProgress = {
        id: `${selectedCrewMember.persona}|${weekDayItem}|${date}|progress`,
        date: date,
        weekDay: weekDayItem,
        milestonehours: bankersRound(milestonehours, 0),
        milestoneQuantity: regularRound(milestoneQuantity, 0),
        milestoneApprovedHours: bankersRound(milestoneApprovedHours, 0),
        milestoneApprovedQuantity: regularRound(milestoneApprovedQuantity, 0),
        cumulativeMilestoneHours: bankersRound(cumulativeMilestoneHours, 0),
        cumulativeMilestoneQuantity: regularRound(cumulativeMilestoneQuantity, 0),
        targetHours: bankersRound(targetHours, 0),
        targetQuantity: regularRound(targetQuantity, 0),
        milestoneClaimedInPlanHours: bankersRound(milestoneClaimedInPlanHours, 0),
        milestoneClaimedInPlanQuantity: regularRound(milestoneClaimedInPlanQuantity, 0),
      };
      return row;
    });
  }

  //Note: Backend sends targetdata with all zero's
  const uniqueTargetData = uniqBy(milestoneProgress, (mp) => mp.targetHours);
  const allZeroTargetData = uniqueTargetData.length == 1 && uniqueTargetData[0].targetHours == 0;

  const weeklyMilestoneAnalysis: WeeklyMilestoneAnalysis = get(
    data,
    'weeklyMilestoneProgress.weeklyMilestoneAnalysis',
    undefined
  );

  const showCumulativeProgressLine = () => {
    return some(milestoneProgress, ({ cumulativeMilestoneHours }) => cumulativeMilestoneHours > 0);
  };

  const getLastWeekDay = () => {
    if (!milestoneProgress || milestoneProgress.length == 0) return null;
    return last(milestoneProgress)['date'];
  };

  const getClaimProgressStartDay = () => {
    if (!selectedProjectWeek || !selectedProjectWeek.id || !milestoneProgress || milestoneProgress.length == 0)
      return null;
    if (!selectedProjectWeek.isCurrent) return getLastWeekDay();
    const today = find(milestoneProgress, (mp) => mp.weekDay == format(new Date(), 'EEEE'));
    if (!today) return null;
    return today.date;
  };

  const getClaimProgressEndDay = () => {
    if (
      !selectedProjectWeek ||
      !selectedProjectWeek.id ||
      !selectedProjectWeek.isCurrent ||
      !milestoneProgress ||
      milestoneProgress.length == 0
    )
      return null;
    const today = find(milestoneProgress, (mp) => mp.weekDay == format(new Date(), 'EEEE'));
    if (!today || today.weekDay == last(milestoneProgress)['weekDay']) return null;
    return nth(milestoneProgress, indexOf(milestoneProgress, today) + 1)?.date;
  };

  const getSecondaryYAxisMax = () => {
    if (
      !weeklyMilestoneAnalysis ||
      (weeklyMilestoneAnalysis.cumulativeMilestoneHours == 0 && weeklyMilestoneAnalysis.targetHours == 0)
    )
      return null;

    return isUnitOfMeasureHour
      ? Math.max(weeklyMilestoneAnalysis.cumulativeMilestoneHours, weeklyMilestoneAnalysis.targetHours) + 100
      : Math.max(weeklyMilestoneAnalysis.cumulativeMilestoneQuantity, weeklyMilestoneAnalysis.targetQuantity.quantity) +
          100;
  };

  const getSecondaryYAxisMin = () => {
    if (!milestoneProgress || milestoneProgress.length == 0) return null;
    const min = minBy(milestoneProgress, (milestoneHours) =>
      isUnitOfMeasureHour ? milestoneHours.cumulativeMilestoneHours : milestoneHours.cumulativeMilestoneQuantity
    );
    return isUnitOfMeasureHour ? min?.cumulativeMilestoneHours : min?.cumulativeMilestoneQuantity;
  };

  const targetValue = weeklyMilestoneAnalysis
    ? isUnitOfMeasureHour
      ? weeklyMilestoneAnalysis?.targetHours
      : regularRound(weeklyMilestoneAnalysis?.targetQuantity.quantity, 0)
    : 0;
  const cumulativeMilestoneValue = weeklyMilestoneAnalysis
    ? isUnitOfMeasureHour
      ? weeklyMilestoneAnalysis?.cumulativeMilestoneHours
      : regularRound(weeklyMilestoneAnalysis?.cumulativeMilestoneQuantity, 0)
    : 0;

  return (
    <>
      {(!selectedProjectWeek || !selectedProjectWeek.id) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
          <Trans>No project week selected</Trans>
        </Box>
      )}
      {(!selectedCrewMember || !selectedCrewMember.persona) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
          <Trans>No user or persona selected</Trans>
        </Box>
      )}
      {loading && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }} data-testid="loading">
          <StatsSpinner />
        </Box>
      )}
      {!loading && error && (
        <ErrorContainer>
          <ErrorRetry refetch={refetch}>
            <Trans>There was error fetching data for claimed progress chart.</Trans>
          </ErrorRetry>
        </ErrorContainer>
      )}
      {!loading &&
        !error &&
        (!data || milestoneProgress.length == 0 || allZeroTargetData) &&
        selectedCrewMember &&
        selectedCrewMember.persona &&
        called && (
          <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
            <Trans>No available claimed progress chart data.</Trans>
          </Box>
        )}
      {/* loading check duplicted here to support chart to adapt for dynamic screen size changes  */}
      {!loading && selectedCrewMember && selectedCrewMember.persona && data && !allZeroTargetData && (
        <Box alignItems={{ sm: 'center' }} pt={{ sm: '400' }} pb={{ sm: '200' }}>
          {/* dual y-axis label moved outside as the position options available in recharts framework doesn't suit the hifi */}
          <Text color="grey.600" size="300" style={{ marginLeft: theme.spacings['800'] }}>
            {`${unitMeasureLabel} (Daily)`}
          </Text>
          <Text color="grey.600" size="300" style={{ marginLeft: 'auto', marginRight: theme.spacings['800'] }}>
            {`${unitMeasureLabel} (Cumulative)`}
          </Text>
        </Box>
      )}
      {!loading && selectedCrewMember && selectedCrewMember.persona && data && !allZeroTargetData && (
        <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }} data-testid="chart-content-container">
          <ResponsiveContainer width="99%" height="99%">
            <ComposedChart
              data={milestoneProgress}
              margin={{
                top: 8,
                bottom: 16,
                left: 16,
                right: 16,
              }}
            >
              <CartesianGrid stroke={String(theme.colors.grey['200'])} vertical={false} />
              <defs>
                <linearGradient id="targetColor" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={String(theme.colors.blue['300'])} stopOpacity={1} />
                  <stop offset="100%" stopColor="#d7e9f2" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="claimedColor" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={String(theme.colors.orange['400'])} stopOpacity={1} />
                  <stop offset="100%" stopColor="#f6c68a" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="ClaimedInPlan" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={String(theme.colors.purple['200'])} stopOpacity={1} />
                  <stop offset="100%" stopColor="#e7d7f9" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="approvedColor" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={String(theme.colors.green['400'])} stopOpacity={1} />
                  <stop offset="100%" stopColor="#7d9b7d" stopOpacity={1} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={<WeekDayXAxis />} />
              <YAxis
                yAxisId="right"
                type="number"
                dataKey={isUnitOfMeasureHour ? `cumulativeMilestoneHours` : `cumulativeMilestoneQuantity`}
                orientation="right"
                axisLine={false}
                tick={{ fontSize: theme.fontSizes['300'] }}
                tickLine={false}
                interval={0}
                domain={[getSecondaryYAxisMin(), getSecondaryYAxisMax()]}
              />
              <YAxis axisLine={false} tick={{ fontSize: theme.fontSizes['300'] }} tickLine={false} interval={0} />
              <Bar
                dataKey={isUnitOfMeasureHour ? `targetHours` : `targetQuantity`}
                barSize={BAR_SIZE}
                fill="url(#targetColor)"
                radius={[4, 4, 0, 0]}
                onMouseEnter={() => setShowHideTooltip(true)}
                onMouseLeave={() => setShowHideTooltip(false)}
              />
              <Bar
                dataKey={isUnitOfMeasureHour ? `milestonehours` : `milestoneQuantity`}
                barSize={BAR_SIZE}
                fill="url(#claimedColor)"
                radius={[4, 4, 0, 0]}
                onMouseEnter={() => setShowHideTooltip(true)}
                onMouseLeave={() => setShowHideTooltip(false)}
              />
              <Bar
                dataKey={isUnitOfMeasureHour ? `milestoneClaimedInPlanHours` : `milestoneClaimedInPlanQuantity`}
                barSize={BAR_SIZE}
                fill="url(#ClaimedInPlan)"
                radius={[4, 4, 0, 0]}
                onMouseEnter={() => setShowHideTooltip(true)}
                onMouseLeave={() => setShowHideTooltip(false)}
              />
              <Bar
                dataKey={isUnitOfMeasureHour ? `milestoneApprovedHours` : `milestoneApprovedQuantity`}
                barSize={BAR_SIZE}
                fill="url(#approvedColor)"
                radius={[4, 4, 0, 0]}
                onMouseEnter={() => setShowHideTooltip(true)}
                onMouseLeave={() => setShowHideTooltip(false)}
              />
              {showCumulativeProgressLine() && (
                <Line
                  type="monotone"
                  yAxisId="right"
                  dataKey={isUnitOfMeasureHour ? `cumulativeMilestoneHours` : `cumulativeMilestoneQuantity`}
                  stroke={String(theme.colors.grey['500'])}
                  strokeWidth="1.5"
                  dot={false}
                />
              )}
              {showHideTooltip && <Tooltip content={<CharToolTip />} isAnimationActive={false} cursor={false} />}
              {weeklyMilestoneAnalysis && targetValue && targetValue != 0 && (
                <ReferenceArea
                  yAxisId="right"
                  x1={getLastWeekDay()}
                  y1={targetValue}
                  y2={targetValue + RHOMBUS_SIZE} //8 is Size of the rhombus
                  shape={
                    <TargetIndicatorReferenceArea
                      targetValue={targetValue}
                      isUnitOfMeasureHour={isUnitOfMeasureHour}
                      unitMeasureLabel={unitMeasureLabel}
                      yAxisMax={getSecondaryYAxisMax()}
                    />
                  }
                />
              )}
              {weeklyMilestoneAnalysis && cumulativeMilestoneValue && cumulativeMilestoneValue != 0 && (
                <ReferenceArea
                  yAxisId="right"
                  x1={getClaimProgressStartDay()}
                  x2={getClaimProgressEndDay()}
                  y1={cumulativeMilestoneValue}
                  y2={cumulativeMilestoneValue + RHOMBUS_SIZE} //8 is Size of the rhombus
                  shape={
                    <ClaimedProgressReferenceArea
                      cumulativeMilestoneValue={cumulativeMilestoneValue}
                      isUnitOfMeasureHour={isUnitOfMeasureHour}
                      unitMeasureLabel={unitMeasureLabel}
                      yAxisMax={getSecondaryYAxisMax()}
                    />
                  }
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </>
  );
};
export const Chart: FC<ChartProps> = ({ selectedProjectWeek, selectedCrewMember }) => {
  return (
    <ChildContainerStyled data-testid="chart-container">
      <ChartHeader selectedProjectWeek={selectedProjectWeek} selectedCrewMember={selectedCrewMember} />
      <ChartContent selectedProjectWeek={selectedProjectWeek} selectedCrewMember={selectedCrewMember} />
    </ChildContainerStyled>
  );
};
